import { useMutation, useQueryClient } from "@tanstack/react-query";

import * as Events from "@app/analytics/tracking/events";
import api from "@app/common/access/ShareReport/api";
import { useAuthenticated } from "@app/common/auth/useAuth";
import { toast } from "@app/common/hooks/useToasts";
import { isResponseErrorWithStatus } from "@app/common/utils";

const useDeleteReport = () => {
  const queryClient = useQueryClient();
  const { user } = useAuthenticated();

  const invalidateSessionQueries = () =>
    queryClient.invalidateQueries({ queryKey: ["sessionsList"] });

  const deleteReportMutation = useMutation({
    mutationFn: (sessionId: string) => api.access.deleteReport(sessionId),
    onSuccess: async () => {
      Events.deleteReport({ reportType: "meeting" });
      await invalidateSessionQueries();
      toast.success("Report successfully deleted!");
    },
    onError: (error) => {
      if (isResponseErrorWithStatus(error, 403)) {
        toast.error("You do not have permission to delete this report.");
      } else {
        toast.error(
          "Unable to delete report. Please refresh the page or try again."
        );
      }
    },
  });

  const removeReportMutation = useMutation({
    mutationFn: (sessionId: string) =>
      api.access.removeReport(sessionId, user.id),
    onSuccess: async () => {
      Events.deleteReport({ reportType: "meeting" });
      await invalidateSessionQueries();
      toast.success("Report successfully removed!");
    },
    onError: (error) => {
      if (isResponseErrorWithStatus(error, 404)) {
        toast.error("Cannot remove report");
      } else {
        toast.error(
          "Unable to remove report. Please refresh the page or try again."
        );
      }
    },
  });

  return {
    deleteReport: deleteReportMutation,
    removeReport: removeReportMutation,
  };
};

export default useDeleteReport;
